import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Grid,
  Collapse,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack,
  Autocomplete,
  Card,
  CardContent,
} from "@mui/material";
import {
  Edit,
  Delete,
  Add,
  UploadFile,
  Refresh,
  Info,
  Build,
  LocationOn,
  ContactPhone,
} from "@mui/icons-material";
import { artisanAPI } from "../../../services/api/artisan";
import { propertyAPI } from "../../../services/api/property";
import { mergeAndSave } from "../../../utils/storageUtils";

const ArtisanTab = ({ initialSearch = "" }) => {
  const [artisans, setArtisans] = useState(() => {
    const saved = localStorage.getItem("artisans");
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [properties, setProperties] = useState(() => {
    const saved = localStorage.getItem("properties");
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState(initialSearch);

  useEffect(() => {
    if (initialSearch) {
      const trimmed = String(initialSearch).trim();
      const asNumber = Number(trimmed);
      if (trimmed !== "" && !Number.isNaN(asNumber)) {
        setSearch(`id=${trimmed}`);
      } else {
        setSearch(initialSearch);
      }
    }
  }, [initialSearch]);

  const [openDialog, setOpenDialog] = useState(false);
  const [openImportInfoDialog, setOpenImportInfoDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentArtisan, setCurrentArtisan] = useState(null);
  const [expandedArtisanId, setExpandedArtisanId] = useState(null);

  const initialFormState = {
    company_name: "",
    trade: "",
    property_ids: [],
    all_properties: false,
    contact_person: "",
    phone: "",
    mobile: "",
    email: "",
    website: "",
    emergency_service: false,
    hourly_rate: "",
  };
  const [formData, setFormData] = useState(initialFormState);

  const fetchArtisans = async () => {
    setLoading(true);
    try {
      const skip = page * rowsPerPage;
      const response = await artisanAPI.getArtisans({
        skip,
        limit: rowsPerPage,
        search,
      });
      setArtisans(response.data);
      mergeAndSave("artisans", response.data);
      setError(null);
    } catch (err) {
      console.error("Fehler beim Abrufen der Handwerker", err);
      setError("Fehler beim Abrufen der Handwerker");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtisans();
  }, [page, rowsPerPage, search]);

  const fetchProperties = async () => {
    try {
      const response = await propertyAPI.getProperties({ limit: 1000 }); // Get all
      setProperties(response.data);
      mergeAndSave("properties", response.data);
    } catch (err) {
      console.error("Fehler beim Laden der Immobilien", err);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (artisan = null) => {
    await fetchProperties();

    if (artisan) {
      setCurrentArtisan(artisan);
      setFormData({
        company_name: artisan.company_name,
        trade: artisan.trade || "",
        property_ids: Array.isArray(artisan.property_ids)
          ? artisan.property_ids
          : artisan.service_area
            ? artisan.service_area.map((p) => p.id)
            : [],
        all_properties: artisan.all_properties || false,
        contact_person: artisan.contact_person || "",
        phone: artisan.phone || "",
        mobile: artisan.mobile || "",
        email: artisan.email || "",
        website: artisan.website || "",
        emergency_service: artisan.emergency_service || false,
        hourly_rate: artisan.hourly_rate || "",
      });
    } else {
      setCurrentArtisan(null);
      setFormData(initialFormState);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentArtisan(null);
    setFormData(initialFormState);
  };

  const handleDeleteClick = (artisan) => {
    setCurrentArtisan(artisan);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await artisanAPI.deleteArtisan(currentArtisan.id);
      fetchArtisans();
      setOpenDeleteDialog(false);
      setCurrentArtisan(null);
    } catch (err) {
      console.error("Fehler beim Löschen des Handwerkers", err);
      setError("Fehler beim Löschen des Handwerkers");
    }
  };

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (currentArtisan) {
        await artisanAPI.updateArtisan(currentArtisan.id, formData);
      } else {
        await artisanAPI.createArtisan(formData);
      }
      fetchArtisans();
      handleCloseDialog();
    } catch (err) {
      console.error("Fehler beim Speichern des Handwerkers", err);
      setError("Fehler beim Speichern des Handwerkers.");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await artisanAPI.importArtisansFromXlsx(file);
      fetchArtisans();
    } catch (err) {
      console.error("Fehler beim Importieren der Handwerker", err);
      setError("Fehler beim Importieren der Handwerker");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Handwerker
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={search ?? ""}
          onChange={handleSearchChange}
          placeholder="Suche nach Firma, Gewerbe..."
        />
        <Box>
          <Button
            variant="outlined"
            onClick={fetchArtisans}
            startIcon={<Refresh />}
            sx={{ mr: 1 }}
          >
            Aktualisieren
          </Button>
          <IconButton
            onClick={() => setOpenImportInfoDialog(true)}
            sx={{ mr: 1 }}
            color="primary"
          >
            <Info />
          </IconButton>
          <input
            accept=".xlsx, .xls"
            style={{ display: "none" }}
            id="raised-button-file"
            data-testid="raised-button-file"
            type="file"
            onChange={handleImport}
          />
          <label htmlFor="raised-button-file">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFile />}
              sx={{ mr: 1 }}
            >
              Import
            </Button>
          </label>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Handwerker hinzufügen
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Firma</TableCell>
              <TableCell>Gewerbe</TableCell>
              <TableCell>Servicebereich</TableCell>
              <TableCell>Kontaktperson</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {artisans.map((artisan) => (
              <React.Fragment key={artisan.id}>
                <TableRow
                  hover
                  onClick={() =>
                    setExpandedArtisanId(
                      expandedArtisanId === artisan.id ? null : artisan.id,
                    )
                  }
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{artisan.company_name}</TableCell>
                  <TableCell>{artisan.trade}</TableCell>
                  <TableCell>
                    {artisan.all_properties
                      ? "Alle Immobilien"
                      : Array.isArray(artisan.service_area) &&
                          artisan.service_area.length > 0
                        ? artisan.service_area.map((p) => p.address).join(", ")
                        : "-"}
                  </TableCell>
                  <TableCell>{artisan.contact_person}</TableCell>
                  <TableCell>{artisan.phone}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(artisan);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(artisan);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={6}
                  >
                    <Collapse
                      in={expandedArtisanId === artisan.id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ margin: 2 }}>
                        <Typography variant="h6" gutterBottom component="div">
                          Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Mobil:</strong> {artisan.mobile || "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>E-Mail:</strong> {artisan.email || "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Webseite:</strong>{" "}
                              {artisan.website || "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Notdienst:</strong>{" "}
                              {artisan.emergency_service ? "Ja" : "Nein"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              <strong>Stundensatz:</strong>{" "}
                              {artisan.hourly_rate
                                ? `${artisan.hourly_rate} €`
                                : "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                        {/* Show property addresses if property_ids exist and not all_properties */}
                        {Array.isArray(artisan.property_ids) &&
                          artisan.property_ids.length > 0 &&
                          !artisan.all_properties && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Zuständig für Immobilien:
                              </Typography>
                              <ul style={{ paddingLeft: 18 }}>
                                {artisan.property_ids.map((pid) => {
                                  const prop = properties.find(
                                    (p) => p.id === pid,
                                  );
                                  return (
                                    <li key={pid}>
                                      <a
                                        href={`/asset-overview?tab=properties&search=${pid}`}
                                        style={{
                                          color: "#1976d2",
                                          textDecoration: "underline",
                                          cursor: "pointer",
                                        }}
                                        rel="noopener noreferrer"
                                      >
                                        {prop
                                          ? `${prop.address} (${prop.property_number})`
                                          : `Immobilie #${pid}`}
                                      </a>
                                    </li>
                                  );
                                })}
                              </ul>
                            </Box>
                          )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {artisans.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Keine Handwerker gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={-1}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Zeilen pro Seite:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
        }
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 1,
            fontSize: "1.1rem",
            p: 3,
          }}
        >
          <Build />
          {currentArtisan
            ? "Handwerker bearbeiten"
            : "Neuen Handwerker erstellen"}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={4}>
              {/* Company Info */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e3f2fd",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <Build color="primary" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Firmeninformationen
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          required
                          label="Firma"
                          name="company_name"
                          value={formData.company_name ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Gewerbe"
                          name="trade"
                          value={formData.trade ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Stundensatz (€)"
                          name="hourly_rate"
                          type="number"
                          value={formData.hourly_rate ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.emergency_service}
                              onChange={handleFormChange}
                              name="emergency_service"
                              color="error"
                            />
                          }
                          label="Notdienst verfügbar"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Service Area */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #fff3e0",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <LocationOn color="warning" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "warning.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Zuständigkeitsbereich
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      {!formData.all_properties && (
                        <Grid item xs={12}>
                          <Autocomplete
                            multiple
                            options={properties}
                            getOptionLabel={(option) =>
                              `${option.address} (${option.property_number})`
                            }
                            value={properties.filter((p) =>
                              formData.property_ids.includes(p.id),
                            )}
                            onChange={(event, newValue) => {
                              handleAutocompleteChange(
                                "property_ids",
                                newValue.map((p) => p.id),
                              );
                            }}
                            renderTags={(tagValue, getTagProps) =>
                              tagValue.map((option, index) => (
                                <Chip
                                  label={`${option.address} (${option.property_number})`}
                                  {...getTagProps({ index })}
                                  size="small"
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Immobilien auswählen"
                                placeholder="Immobilien suchen..."
                                variant="outlined"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    minWidth: 300,
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.all_properties}
                              onChange={handleFormChange}
                              name="all_properties"
                              color="primary"
                            />
                          }
                          label="Für alle Immobilien zuständig"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Contact Info */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e8f5e8",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <ContactPhone color="success" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "success.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Kontakt
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Kontaktperson"
                          name="contact_person"
                          value={formData.contact_person ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="E-Mail"
                          name="email"
                          value={formData.email ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Telefon"
                          name="phone"
                          value={formData.phone ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Mobil"
                          name="mobile"
                          value={formData.mobile ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Website"
                          name="website"
                          value={formData.website ?? ""}
                          onChange={handleFormChange}
                          placeholder="https://"
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} variant="contained" size="large">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Bestätigung löschen</DialogTitle>
        <DialogContent>
          Sind Sie sicher, dass Sie den Handwerker{" "}
          {currentArtisan?.company_name}
          löschen möchten?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Abbrechen</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openImportInfoDialog}
        onClose={() => setOpenImportInfoDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Format Informationen</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Für den Import von Handwerkern verwenden Sie bitte eine Excel-Datei
            (.xlsx) mit dem folgenden Spaltenaufbau (ab Zeile 2):
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Spalte</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Beschreibung</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Datentyp / Format</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>A (0)</TableCell>
                  <TableCell>Firma</TableCell>
                  <TableCell>Text (Pflichtfeld)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>B (1)</TableCell>
                  <TableCell>Tätigkeit</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>C (2)</TableCell>
                  <TableCell>Zuständigkeitsgebiet</TableCell>
                  <TableCell>
                    Text (IDs durch Komma getrennt oder "alle" / "allgemein")
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>D (3)</TableCell>
                  <TableCell>Ansprechpartner</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>E (4)</TableCell>
                  <TableCell>Telefonnummer</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>F (5)</TableCell>
                  <TableCell>Mobilnummer</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>G (6)</TableCell>
                  <TableCell>E-Mail</TableCell>
                  <TableCell>E-Mail Adresse (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>H (7)</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell>URL (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>I (8)</TableCell>
                  <TableCell>Notdienst</TableCell>
                  <TableCell>Ja/Nein (Optional, Standard: Nein)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>J (9)</TableCell>
                  <TableCell>Stundensatz</TableCell>
                  <TableCell>Zahl (Optional)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Hinweise:</Typography>
            <ul>
              <li>Die erste Zeile wird als Überschrift ignoriert.</li>
              <li>
                Für "Zuständigkeitsgebiet" können Sie "alle" oder "allgemein"
                verwenden, um den Handwerker allen Immobilien zuzuweisen.
              </li>
              <li>
                Anderenfalls geben Sie die IDs der Immobilien kommagetrennt an
                (z.B. "1, 5, 12").
              </li>
              <li>
                Für "Notdienst" verwenden Sie "ja", "yes", "true", "1" oder "x"
                für Ja, lassen Sie das Feld leer oder verwenden Sie andere Werte
                für Nein.
              </li>
              <li>
                Für "Stundensatz" verwenden Sie Zahlen wie "25.50" oder "25,50"
                (Kommas werden automatisch in Punkte umgewandelt).
              </li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImportInfoDialog(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArtisanTab;
